import { Injectable, Injector, inject } from "@angular/core";
import { Router } from "@angular/router";
import {
  ActionSheetController,
  AlertController,
  LoadingController,
  MenuController,
  ModalController,
  NavController,
  PopoverController,
} from "@ionic/angular";
import { FlightTimerService } from "../flight-timer/flight-timer.service";
import { opsControlService } from "../ops-control/ops-control.service";
import { TableChangeNotifierService } from "../../sync/table-change-notifier.service";
import { AUTO_NAVIG_WINDOW_NAME, firstNavFlightIds } from "./auto-navig";

const OPEN_ATTEMPTS = 3;
const OPEN_RETRY_MS = 5000;

/**
 * Wallboard window named `efb-auto-navig`: every new flight opens NAVIG and
 * discards whatever the window showed. Does nothing in any other window.
 * A flight is new when its first nav log arrives in a sync frame. A web write
 * comes back to the window that wrote it too, so this window's own creates
 * arrive here; they are skipped while the create runs.
 */
@Injectable({ providedIn: "root" })
export class AutoNavigService {
  private notifier = inject(TableChangeNotifierService);
  private opsCtrlSer = inject(opsControlService);
  private navCtrl = inject(NavController);
  private router = inject(Router);
  private menu = inject(MenuController);
  private modal = inject(ModalController);
  private alert = inject(AlertController);
  private popover = inject(PopoverController);
  private actionSheet = inject(ActionSheetController);
  private loading = inject(LoadingController);
  private injector = inject(Injector);

  private queue: Promise<void> = Promise.resolve();
  // A newer flight cancels the retries of an older one.
  private generation = 0;
  // True once a flight loaded in NAVIG.
  private opened = false;

  constructor() {
    if (typeof window === "undefined" || window.name !== AUTO_NAVIG_WINDOW_NAME) return;
    this.notifier.onChange("nav").subscribe((c) => {
      const ids = firstNavFlightIds(c.ops);
      if (ids.length > 0) this.enqueue(ids[ids.length - 1], ++this.generation, 1);
    });
  }

  /** Opens NAVIG for a flight that was already active when the window loaded.
   *  It waits behind the opens of new flights and does nothing once one of them
   *  loaded: that flight is the newer one. An open that failed or was skipped
   *  leaves this one its try. ponytail: one try and no retry, so it can never
   *  load over a newer flight later. */
  open(flightId: string): void {
    this.queue = this.queue.then(() => (this.opened ? undefined : this.tryOpen(flightId, this.generation, 1, true)));
  }

  private enqueue(flightId: string, gen: number, attempt: number): void {
    this.queue = this.queue.then(() => this.tryOpen(flightId, gen, attempt));
  }

  private async tryOpen(flightId: string, gen: number, attempt: number, once = false): Promise<void> {
    if (gen !== this.generation) return;
    // A running timer stamps its landing on the flight loaded in the map, so
    // never switch that flight. Lazy: FlightTimerService injects ops-control.
    if (this.injector.get(FlightTimerService).isTimerRunning()) {
      console.warn("[autoNavig] flight timer running, NAVIG skipped for", flightId);
      return;
    }
    // Never interrupt a running flight create in this window: its own first nav
    // log comes back here mid-create. A form left open with no create running
    // is taken over like any other screen. ponytail: a flight from elsewhere
    // that lands during those seconds is skipped too.
    if (this.opsCtrlSer.flightCreateRunning) {
      console.warn("[autoNavig] flight create running, NAVIG skipped for", flightId);
      return;
    }
    // A retry only reloads the flight. It never closes a dialog or leaves a
    // page, and it gives up once the operator left the map or edits a route.
    const retry = attempt > 1;
    if (retry && (!this.router.url.startsWith("/map") || this.opsCtrlSer.MapInEditMode)) return;
    try {
      if (await this.openNavig(flightId, !retry)) return;
    } catch (err) {
      console.error("[autoNavig] open failed:", err);
    }
    if (!once && attempt < OPEN_ATTEMPTS) setTimeout(() => this.enqueue(flightId, gen, attempt + 1), OPEN_RETRY_MS);
  }

  private async dismissOverlays(): Promise<void> {
    const ctrls = [this.modal, this.alert, this.popover, this.actionSheet, this.loading];
    // Repeat until a pass closes nothing: a tap through a closing overlay can open
    // another one (Create opens the POB numpad). ponytail: 3 passes, then give up.
    for (let pass = 0; pass < 3; pass++) {
      let closed = false;
      for (const ctrl of ctrls) {
        for (;;) {
          try {
            const top = await ctrl.getTop();
            if (!top) break;
            await top.dismiss();
            closed = true;
          } catch (err) {
            console.error("[autoNavig] dismiss failed:", err);
            break;
          }
        }
      }
      if (!closed) return;
    }
  }

  /** True when nothing is left to retry: the flight loaded, or a flight create
   *  started in this window. `takeOver` first discards the current view. */
  private async openNavig(flightId: string, takeOver: boolean): Promise<boolean> {
    if (takeOver) {
      await this.dismissOverlays();
      // A Create pressed while the overlays closed keeps its window. Skipped for
      // good, like the check in tryOpen: no retry.
      if (this.opsCtrlSer.flightCreateRunning) {
        console.warn("[autoNavig] flight create started, NAVIG skipped for", flightId);
        return true;
      }
      this.opsCtrlSer.MapInEditMode = false;
    }
    this.opsCtrlSer.focusPlannedRouteFlightId = flightId;
    let loaded = false;
    try {
      if (takeOver) await this.navCtrl.navigateRoot("/map");
      loaded = !!(await this.opsCtrlSer.setCurrentFlightInMapView(flightId, true));
      if (loaded) this.opened = true;
      else this.opsCtrlSer.focusPlannedRouteFlightId = undefined;
    } catch (err) {
      this.opsCtrlSer.focusPlannedRouteFlightId = undefined;
      throw err;
    }
    this.opsCtrlSer.routingState = "loadExplicitFlight";
    // The map is the root page here, so it has no back button: the menu must stay on.
    await this.menu.enable(true);
    return loaded;
  }
}
